import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { environment } from '@env/environment';
import { LISTING_SEO_DEFAULT_TITLE } from '@app/utils/listing-seo.util';
import { upsertFacebookAppId } from '@app/utils/social-meta.util';
import {
  buildSiteCanonicalUrl,
  isListingDetailPath,
  resolveSiteSeoEntry,
  SiteSeoEntry,
} from '@app/utils/site-seo.util';

const SITE_CANONICAL_LINK_ID = 'site-canonical-link';
const SITE_JSON_LD_SCRIPT_ID = 'site-json-ld';

@Injectable({
  providedIn: 'root',
})
export class SiteSeoService {
  private started = false;
  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  init(): void {
    if (this.started) {
      return;
    }

    this.started = true;
    upsertFacebookAppId(this.meta, environment.FACEBOOK_APP_ID);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.applyForUrl(event.urlAfterRedirects);
      });

    if (isPlatformBrowser(this.platformId)) {
      this.applyForUrl(this.router.url);
    }
  }

  applyForUrl(url: string): void {
    if (isListingDetailPath(url)) {
      return;
    }

    const entry = resolveSiteSeoEntry(url);
    if (entry) {
      this.applyEntry(entry);
      return;
    }

    this.clear();
  }

  private applyEntry(entry: SiteSeoEntry): void {
    this.title.setTitle(entry.title);
    this.upsertMetaTag('name', 'description', entry.description);
    this.upsertMetaTag('property', 'og:title', entry.title);
    this.upsertMetaTag('property', 'og:description', entry.description);
    this.upsertMetaTag('property', 'og:url', buildSiteCanonicalUrl(this.resolveSiteOrigin(), entry));
    this.upsertMetaTag('property', 'og:type', 'website');
    this.upsertMetaTag('property', 'og:site_name', LISTING_SEO_DEFAULT_TITLE);
    this.upsertMetaTag('name', 'twitter:card', entry.ogImagePath ? 'summary_large_image' : 'summary');
    this.upsertMetaTag('name', 'twitter:title', entry.title);
    this.upsertMetaTag('name', 'twitter:description', entry.description);
    const siteOrigin = this.resolveSiteOrigin();
    if (entry.ogImagePath && siteOrigin) {
      const imageUrl = `${siteOrigin}${entry.ogImagePath}`;
      this.upsertMetaTag('property', 'og:image', imageUrl);
      this.upsertMetaTag('name', 'twitter:image', imageUrl);
    } else {
      this.removeMetaTag('property="og:image"');
      this.removeMetaTag('name="twitter:image"');
    }
    this.removeMetaTag('name="robots"');
    this.setCanonicalLink(buildSiteCanonicalUrl(siteOrigin, entry));
    this.document.getElementById('listing-json-ld')?.remove();
    if (entry.jsonLdBuilder && siteOrigin) {
      this.setJsonLd(entry.jsonLdBuilder(siteOrigin));
    } else {
      this.removeJsonLd();
    }
  }

  clear(): void {
    this.title.setTitle(LISTING_SEO_DEFAULT_TITLE);
    this.removeMetaTag('name="description"');
    this.removeMetaTag('property="og:title"');
    this.removeMetaTag('property="og:description"');
    this.removeMetaTag('property="og:url"');
    this.removeMetaTag('property="og:type"');
    this.removeMetaTag('property="og:site_name"');
    this.removeMetaTag('property="og:image"');
    this.removeMetaTag('name="twitter:card"');
    this.removeMetaTag('name="twitter:title"');
    this.removeMetaTag('name="twitter:description"');
    this.removeMetaTag('name="twitter:image"');
    this.removeMetaTag('name="robots"');
    this.removeCanonicalLink();
    this.removeJsonLd();
  }

  private resolveSiteOrigin(): string {
    const configured = environment.SITE_URL?.trim();
    if (configured) {
      return configured.replace(/\/$/, '');
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }

    return '';
  }

  private upsertMetaTag(
    attribute: 'name' | 'property',
    key: string,
    content: string,
  ): void {
    const selector = `${attribute}="${key}"`;
    const existing = this.meta.getTag(selector);

    if (existing) {
      this.meta.updateTag({ [attribute]: key, content });
      return;
    }

    this.meta.addTag({ [attribute]: key, content });
  }

  private removeMetaTag(selector: string): void {
    const tag = this.meta.getTag(selector);
    if (tag) {
      this.meta.removeTagElement(tag);
    }
  }

  private setCanonicalLink(url: string): void {
    const head = this.document.head;
    let link = this.document.getElementById(SITE_CANONICAL_LINK_ID) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.id = SITE_CANONICAL_LINK_ID;
      link.rel = 'canonical';
      head.appendChild(link);
    }

    link.href = url;
  }

  private removeCanonicalLink(): void {
    this.document.getElementById(SITE_CANONICAL_LINK_ID)?.remove();
  }

  private setJsonLd(jsonLd: Record<string, unknown>): void {
    const head = this.document.head;
    let script = this.document.getElementById(SITE_JSON_LD_SCRIPT_ID) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script.id = SITE_JSON_LD_SCRIPT_ID;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }

    script.textContent = JSON.stringify(jsonLd);
  }

  private removeJsonLd(): void {
    this.document.getElementById(SITE_JSON_LD_SCRIPT_ID)?.remove();
  }
}
